import {
  type EventContentObjectDetail,
  type Profile,
  type ProgramEventInvitee,
  isRegisteredProgramInvitee,
} from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Icon, type MenuProps } from 'ui';
import { useDirectMessage } from 'features/chat';
import { useGetRailsEventRoleActions } from '../../../../hooks';
import { useDeleteEventInvitee } from './useDeleteEventInvitee';
import { useUpdateEventInviteeRole } from './useUpdateEventInviteeRole';

export const useInviteesActions = () => {
  const { t } = useTranslation();
  const { confirm: deleteEventInvitee, contextHolder, isLoadingDeletingRole } = useDeleteEventInvitee();
  const { isLoadingChangingRole, updateRole: updateEventInviteeRole } = useUpdateEventInviteeRole();
  const { handleDirectMessage, isFetching: isFetchingDirectMessage } = useDirectMessage();
  const getRoleActions = useGetRailsEventRoleActions();
  const { canChangeUserRoleInEvent, canDeleteRailsUserFromEvent } = usePermissions();

  const getInviteesActions = useCallback(
    ({
      currentInvitee,
      eventContentObject,
      viewer,
    }: {
      currentInvitee: ProgramEventInvitee;
      eventContentObject: EventContentObjectDetail;
      viewer: Profile;
    }) => {
      const actions: MenuProps['items'] = [];
      const { eventType, id } = eventContentObject.event;

      if (isRegisteredProgramInvitee(currentInvitee) && viewer.id === currentInvitee.id) {
        return [];
      }

      if (canChangeUserRoleInEvent({ contentObject: eventContentObject })) {
        const roleActions = getRoleActions({ eventType })
          .filter(({ role }) => currentInvitee.eventRole !== role)
          .map(({ key, label, role }) => ({
            icon: <Icon icon="user-edit-line" />,
            key,
            label: t(label),
            async onClick() {
              await updateEventInviteeRole({
                eventId: id,
                invitee: currentInvitee,
                role,
              });
            },
          }));

        actions.push(...roleActions);
      }

      if (isRegisteredProgramInvitee(currentInvitee)) {
        actions.push({
          icon: <Icon icon="comment-line" />,
          key: 'message',
          label: t('Message'),
          onClick() {
            handleDirectMessage({ id: currentInvitee.id });
          },
        });
      }

      if (canDeleteRailsUserFromEvent({ contentObject: eventContentObject })) {
        actions.push({
          danger: true,
          icon: <Icon icon="minus-circle-line" />,
          key: 'remove',
          label: t('Remove from event'),
          onClick() {
            deleteEventInvitee({ eventId: id, invitee: currentInvitee });
          },
        });
      }

      return actions;
    },
    [
      canChangeUserRoleInEvent,
      canDeleteRailsUserFromEvent,
      deleteEventInvitee,
      getRoleActions,
      handleDirectMessage,
      t,
      updateEventInviteeRole,
    ],
  );

  return {
    contextHolder,
    getInviteesActions,
    isLoadingAction: isFetchingDirectMessage || isLoadingDeletingRole || isLoadingChangingRole,
  };
};
